import React, { useEffect, useState } from "react";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchExamDetails, submitExamAnswers } from "../../redux/slices/examSessionSlice";
import { fetchExamsBySubject } from "../../redux/slices/examsSlice";
import { FaClock, FaPaperPlane } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./StartExam.css";

function parseDuration(durationStr) {
    // يدعم "HH:MM:SS" أو "HH:MM" أو "MM:SS" أو "MM"
    console.log('=== parseDuration called ===');
    console.log('Input durationStr:', durationStr);
    console.log('Input type:', typeof durationStr);
    
    if (!durationStr) {
        console.log('durationStr is falsy, returning 0');
        return 0;
    }
    
    const parts = durationStr.split(":").map(Number);
    console.log('Parts after split:', parts);
    
    let result = 0;
    
    if (parts.length === 3) {
        // HH:MM:SS
        result = parts[0] * 3600 + parts[1] * 60 + parts[2];
        console.log('Format: HH:MM:SS, result:', result);
    } else if (parts.length === 2) {
        // إذا كان الجزء الأول كبير جداً (>= 60) اعتبرها MM:SS، غير ذلك HH:MM
        if (parts[0] >= 60) {
            result = parts[0] * 60 + parts[1]; // MM:SS
            console.log('Assumed format: MM:SS, result:', result);
        } else {
            result = parts[0] * 3600 + parts[1] * 60; // HH:MM
            console.log('Assumed format: HH:MM, result:', result);
        }
    } else if (parts.length === 1) {
        // MM only
        result = parts[0] * 60;
        console.log('Format: MM only, result:', result);
    }
    
    console.log('Final result in seconds:', result);
    console.log('Final result in minutes:', result / 60);
    console.log('Final result in hours:', result / 3600);
    
    return result;
}

function formatTime(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    
    if (h > 0) {
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    } else {
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
}

function StartExam() {
    console.log('StartExam component rendered');
    
    const { isDarkMode } = useDarkMode();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { examDetails, loading, error, submitResult } = useSelector((state) => state.examSession);
    const { exams } = useSelector((state) => state.exams);
    
    console.log('examDetails from Redux:', examDetails);
    console.log('loading:', loading);
    console.log('error:', error);
    
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [timeLeft, setTimeLeft] = useState(1800); // القيمة الافتراضية: 30 دقيقة

    useEffect(() => {
        window.scrollTo(0, 0);
        console.log('StartExam useEffect - id:', id);
        if (id) {
            console.log('Dispatching fetchExamDetails...');
            dispatch(fetchExamDetails(id));
            // جلب البيانات من exams slice أيضاً
            dispatch(fetchExamsBySubject({ subjectId: 0, page: 1 }));
        }
    }, [dispatch, id]);

    // تعيين وقت الامتحان عندما يتم تحميل التفاصيل
    useEffect(() => {
        console.log('=== useEffect for examDetails and exams ===');
        console.log('examDetails:', examDetails);
        console.log('exams:', exams);
        console.log('current exam id:', id);
        
        let durationTime = null;
        
        // البحث عن duration_time في examDetails أولاً
        if (examDetails?.duration_time) {
            durationTime = examDetails.duration_time;
            console.log('Found duration_time in examDetails:', durationTime);
        } else {
            // البحث عن duration_time في exams data
            const currentExam = exams?.find(exam => exam.id == id);
            console.log('Current exam from exams:', currentExam);
            
            if (currentExam?.duration_time) {
                durationTime = currentExam.duration_time;
                console.log('Found duration_time in exams:', durationTime);
            }
        }
        
        if (durationTime) {
            console.log('Parsing duration_time:', durationTime);
            const durationSeconds = parseDuration(durationTime);
            console.log('durationSeconds:', durationSeconds);
            console.log('timeLeft before:', timeLeft);
            
            if (durationSeconds > 0) {
                setTimeLeft(durationSeconds);
                console.log('timeLeft after:', durationSeconds);
            }
        } else {
            console.log('No duration_time found anywhere');
        }
    }, [examDetails, exams, id]);

    useEffect(() => {
        if (submitResult) {
            toast.success("✅ تم إرسال الإجابات بنجاح!");
            setTimeout(() => navigate("/exams"), 2000);
        }
    }, [submitResult, navigate]);

    // عداد تنازلي
    useEffect(() => {
        if (timeLeft <= 0) {
            toast.error("تم انتهاء الوقت!");
            setTimeout(() => navigate("/exams"), 2000);
            return;
        }
        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [timeLeft, navigate]);

    const handleAnswerSelect = (questionId, answerId) => {
        setSelectedAnswers(prev => ({
            ...prev,
            [questionId]: answerId
        }));
    };

    const handleSubmit = async () => {
        if (!examDetails?.questions) return;
    
        const answers = Object.entries(selectedAnswers).map(([question_id, answer_id]) => ({
            question_id: Number(question_id),
            answer_id: Number(answer_id)
        }));
    
        const res = await dispatch(submitExamAnswers({ examId: id, answers }));
    
        if (res.meta.requestStatus === "fulfilled") {
            toast.success("✅ تم إرسال الإجابات بنجاح!");
            setTimeout(() => navigate("/exams"), 2000);
        } else {
            toast.error(res.payload || "❌ فشل إرسال الإجابات");
        }
    };
    
    

    return (
        <div className={`questions-section ${isDarkMode ? "dark-mode" : "light-mode"}`}>
            <ToastContainer />
            <div className="container">
                {/* ✅ Breadcrumb */}
                <div className="lessons-breadcrumb">
                    <h1 className="breadcrumb-title">الاختبارات</h1>
                    <div className="breadcrumb-links2">
                        <span className="breadcrumb-link">الرئيسية</span>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-current">الاختبارات</span>
                    </div>
                </div>

                {/* ✅ العنوان والوقت */}
                <div className="exam-header">
                    <h2>{examDetails?.test_title || "عنوان الاختبار"}</h2>
                    <div className="exam-time"><FaClock /> {formatTime(timeLeft)}</div>
                </div>

                {/* ✅ حالة التحميل أو الخطأ */}
                {loading && <div className="text-center my-4">جاري التحميل...</div>}
                {error && <div className="text-center my-4" style={{ color: 'red' }}>{error}</div>}

                {/* ✅ عرض الأسئلة */}
                {examDetails?.questions && examDetails.questions.length > 0 ? (
                    examDetails.questions.map((q, idx) => (
                        <div key={q.id} className="question-box">
                            <h4 className="question-title">السؤال {idx + 1}</h4>
                            <p className="question-text">{q.title}</p>
                            {q.image && (
                                <img src={q.image} alt="صورة السؤال" style={{ maxWidth: 320, borderRadius: 12, margin: '10px 0' }} />
                            )}
                            <div className="answers-list">
                                {q.answers.map((ans) => (
                                    <div
                                        key={ans.id}
                                        className={`answer-item ${selectedAnswers[q.id] === ans.id ? "selected" : ""}`}
                                        onClick={() => !showResults && handleAnswerSelect(q.id, ans.id)}
                                    >
                                        <span className="answer-label">{String.fromCharCode(65 + q.answers.indexOf(ans))}</span>
                                        <span className="answer-text">{ans.answer}</span>
                                        {ans.image && (
                                            <img src={ans.image} alt="إجابة" style={{ maxWidth: 120, borderRadius: 8, marginRight: 10 }} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : !loading && (
                    <div className="text-center my-4">لا توجد أسئلة متاحة لهذا الامتحان</div>
                )}

                {/* ✅ زرار الإرسال */}
                {examDetails?.questions && (
                    <div className="submit-container">
                        <button
                            className="submit-btn"
                            onClick={handleSubmit}
                            disabled={loading || showResults}
                        >
                            <FaPaperPlane />
                            <span>إرسال الإجابات</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StartExam;
